import { FC, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Grid,
	MenuItem,
	TextField,
	Typography,
} from '@mui/material';
import { api } from 'services/api';
import { TPgPaymentChannel, TPgPayabilityRule } from 'services/api/paymentGateway/types';
import { PgPanel } from '../../common/PgPanel';
import { usePgCall } from '../../common/usePgCall';
import { PG_CHANNEL_OPTIONS } from '../../constants';
import { formatDate, formatDateTime, formatMoney } from '../../utils';

type TTestOnInvoiceProps = {
	rules: TPgPayabilityRule[] | null;
};

/** `YYYY-MM-DDTHH:mm` typed in a `datetime-local` input, read as UTC. */
const toUtcIso = (value: string): string | null => (value ? `${value}:00+00:00` : null);

/** "Test on invoice": checks the saved rules against a fresh EAS invoice (`evaluate`, 03 §4.2). */
export const TestOnInvoice: FC<TTestOnInvoiceProps> = ({ rules }) => {
	const [invoiceId, setInvoiceId] = useState('');
	const [channel, setChannel] = useState<TPgPaymentChannel>('link');
	const [at, setAt] = useState('');

	const evaluateCall = usePgCall(api.paymentGateway.evaluatePayability, {
		fallbackError: 'Failed to evaluate the rules',
	});

	const parsedInvoiceId = Number(invoiceId);
	const invoiceIdValid = /^\d+$/.test(invoiceId.trim()) && parsedInvoiceId > 0;

	const handleEvaluate = () => {
		if (!invoiceIdValid) return;
		void evaluateCall.run({ invoiceId: parsedInvoiceId, channel, at: toUtcIso(at) });
	};

	const result = evaluateCall.result;
	const matchedRule = result?.ruleId ? rules?.find((rule) => rule.id === result.ruleId) : null;

	return (
		<PgPanel title="Test on invoice">
			<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
				Reads the invoice from EAS and checks the saved active rules for the chosen channel and
				moment (UTC). Unsaved changes are not taken into account.
			</Typography>
			<Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
				<TextField
					label="EAS invoice id"
					size="small"
					value={invoiceId}
					onChange={(event) => setInvoiceId(event.target.value)}
					error={!!invoiceId && !invoiceIdValid}
					helperText="InvoiceInternalId, not the invoice number"
					inputProps={{ inputMode: 'numeric' }}
					sx={{ width: 200 }}
				/>
				<TextField
					select
					label="Channel"
					size="small"
					value={channel}
					onChange={(event) => setChannel(event.target.value as TPgPaymentChannel)}
					sx={{ minWidth: 280 }}
				>
					{PG_CHANNEL_OPTIONS.map((option) => (
						<MenuItem key={option.value} value={option.value}>
							{option.label}
						</MenuItem>
					))}
				</TextField>
				<TextField
					label="At (UTC)"
					type="datetime-local"
					size="small"
					value={at}
					onChange={(event) => setAt(event.target.value)}
					helperText="Empty — now"
					InputLabelProps={{ shrink: true }}
				/>
				<Button
					variant="contained"
					onClick={handleEvaluate}
					disabled={!invoiceIdValid || evaluateCall.loading}
					startIcon={evaluateCall.loading ? <CircularProgress size={16} /> : undefined}
				>
					Test
				</Button>
			</Box>

			{result && (
				<Box sx={{ mt: 2 }}>
					<Alert severity={result.allowed ? 'success' : 'warning'} sx={{ mb: 2 }}>
						{result.allowed ? (
							<>
								Payment is <b>allowed</b> for this channel at {formatDateTime(result.at)}.
							</>
						) : (
							<>
								Payment is <b>refused</b> at {formatDateTime(result.at)}
								{result.ruleId ? (
									<>
										{' '}
										by rule #{result.ruleId}
										{matchedRule ? ` “${matchedRule.name}”` : ''}
									</>
								) : null}
								: “{result.message}”
							</>
						)}
					</Alert>
					<Grid container spacing={1}>
						{[
							['Invoice number', String(result.invoice.invoiceNumber)],
							['Client', `${result.invoice.clientNumber} ${result.invoice.clientName ?? ''}`],
							['EAS service type', result.invoice.serviceType],
							[
								'Service',
								result.service
									? `${result.service.name} (${result.service.code})`
									: 'Not mapped — the invoice cannot be paid',
							],
							['Due date', formatDate(result.invoice.dueDate)],
							['Payment status', result.invoice.paymentStatus],
							['Total', formatMoney(result.invoice.totalEur, 'EUR')],
							['Remaining', formatMoney(result.invoice.remainingEur, 'EUR')],
						].map(([label, value]) => (
							<Grid item xs={12} sm={6} md={3} key={label}>
								<Typography variant="caption" color="text.secondary" component="div">
									{label}
								</Typography>
								<Typography variant="body2">{value}</Typography>
							</Grid>
						))}
					</Grid>
				</Box>
			)}
		</PgPanel>
	);
};
